'use client';

import Image from 'next/image';
import Link from 'next/link';

import { handleFormatDate, toClassName } from 'helpers/format';

import FadeInAnimation from 'components/fade-in-animation';
import PageBody from 'components/page-body';

import { styles } from '.';
import { useEffect, useState } from 'react';

import Icon from '@repo/ui/icon';
import { useTranslation } from 'react-i18next';

type TProps = {
  locale: string;
  //
  heroSection: {
    bar: any | null;
    event: any | null;
    article: any | null;
  };
};

const HomeHero = (props: TProps) => {
  const { locale, heroSection } = props;

  const { t } = useTranslation(locale);

  const [isVideoShow, setIsVideoShow] = useState(false);

  if (heroSection === undefined || heroSection === null) {
    return;
  }

  const handleGenerateSpotlight = () => {
    let result;

    if (heroSection?.event !== undefined && heroSection?.event !== null) {
      result = {
        title: heroSection?.event.title,
        subtitle: heroSection?.event.location,
        posterUrl: heroSection?.event?.poster_image?.asset?.url,
        bannerUrl: heroSection?.event?.banner_image?.asset?.url,
        videoUrl: heroSection?.event?.banner_video_url,
        logo: heroSection?.event.logo.asset.url,
        href: `/${locale}/events/${heroSection?.event.slug}`,
        color: heroSection?.event?.color,
        from: heroSection?.event?.from,
        until: heroSection?.event?.until,
      };
    }

    if (heroSection?.bar !== undefined && heroSection?.bar !== null) {
      result = {
        title: heroSection?.bar.title,
        subtitle: heroSection?.bar.location,
        posterUrl: heroSection?.bar.cover.asset.url,
        bannerUrl: heroSection?.bar.cover.asset.url,
        href: `/${locale}/events/${heroSection?.bar.slug}`,
      };
    }

    if (heroSection?.article !== undefined && heroSection?.article !== null) {
      result = {
        title: heroSection?.article.title,
        subtitle: heroSection?.article.date,
        posterUrl: heroSection?.article.cover.asset.url,
        bannerUrl: heroSection?.article.cover.asset.url,
        href: `/${locale}/events/${heroSection?.article.slug}`,
      };
    }

    return result;
  };

  const spotlight = handleGenerateSpotlight();

  // const color = '#CCC';
  //event?.preview_section?.color ??

  const hasVideo =
    spotlight?.videoUrl !== undefined && spotlight?.videoUrl !== null;
  const hasLogo = spotlight?.logo !== undefined && spotlight?.logo !== null;

  useEffect(() => {
    let timeout: any;

    if (hasVideo && !isVideoShow) {
      timeout = setTimeout(() => {
        setIsVideoShow((value) => !value);
      }, 5000);
    }

    if (hasVideo && isVideoShow) {
      timeout = setTimeout(() => {
        setIsVideoShow((value) => !value);
      }, 10000);
    }

    return () => clearInterval(timeout);
  }, [hasVideo, isVideoShow]);

  return (
    <>
      <div className={styles.container}>
        <div
          className={styles.backdrop}
          style={{ backgroundColor: spotlight?.color }}
        />
        <div className={styles.body.background.container}>
          <Image
            src={spotlight?.bannerUrl}
            alt="hero-picture"
            fill
            className={toClassName(
              styles.body.image,
              'hidden sm:block',
              isVideoShow ? '!opacity-0 sm:!opacity-100' : 'opacity-100',
              hasVideo && 'sm:group-hover:!opacity-0',
            )}
          />

          <Image
            src={spotlight?.posterUrl}
            alt="hero-picture"
            fill
            className={toClassName(
              styles.body.image,
              'block sm:hidden',
              isVideoShow ? '!opacity-0 sm:!opacity-100' : 'opacity-100',
              hasVideo && 'sm:group-hover:!opacity-0',
            )}
          />

          {hasVideo && (
            <video
              className={toClassName(
                'w-full h-full object-cover duration-1000',
                isVideoShow ? '!opacity-100' : 'opacity-0 sm:!opacity-100',
              )}
              autoPlay
              loop
              muted
              playsInline
              src={spotlight?.videoUrl}
            />
          )}

          {/* <div
            className={styles.gradient}
            style={{
              backgroundImage: `linear-gradient(to top, ${spotlight?.color}, transparent)`,
            }}
          /> */}

          <div className={styles.logo}>
            {hasLogo && (
              <Image
                src={spotlight?.logo}
                alt="venue-cover"
                width={500}
                height={500}
                // className=""
                className={toClassName(
                  'w-[80vw] sm:w-auto sm:max-w-[40rem] duration-1000 -translate-y-[5rem] sm:translate-y-4',
                  isVideoShow ? '!opacity-0 sm:!opacity-100' : 'opacity-100',
                  hasVideo && 'sm:group-hover:!opacity-0',
                )}
              />
            )}
          </div>
        </div>
        <PageBody className="!py-0">
          <div className={styles.body.container}>
            <div className={styles.body.content.container}>
              <div className={styles.body.content.foot}>
                <div
                  className={toClassName(
                    'py-8 xl:py-16 flex flex-col duration-1000',
                    // 'w-[10rem] -translate-y-12 sm:w-auto sm:-translate-y-6 duration-1000',
                    // isVideoShow ? '!opacity-100' : 'opacity-0',
                  )}
                >
                  <FadeInAnimation
                    as="div"
                    className="flex flex-col items-start"
                  >
                    {/* {hasLogo && (
                      <Image
                        src={spotlight?.logo}
                        alt="venue-cover"
                        width={150}
                        height={150}
                        // className={toClassName(
                        //   'duration-1000',
                        //   // 'w-[10rem] -translate-y-12 sm:w-auto sm:-translate-y-6 duration-1000',
                        //   isVideoShow ? '!opacity-100' : 'opacity-0',
                        // )}
                      />
                    )} */}
                    <div className={styles.date}>
                      <p>{handleFormatDate(spotlight?.from, t)}</p>
                      <Icon
                        accessor="arrow-right"
                        className={styles.icon}
                        size="10px"
                      />
                      <p className="">
                        {handleFormatDate(spotlight?.until, t)}
                      </p>
                    </div>
                  </FadeInAnimation>

                  <FadeInAnimation as="h1" className={styles.title}>
                    {spotlight?.title}
                  </FadeInAnimation>

                  <FadeInAnimation as="h1">
                    <span className={styles.subtitle}>
                      {spotlight?.subtitle}
                    </span>
                  </FadeInAnimation>
                </div>
              </div>

              <div className={styles.body.actions.container}>
                <Link href={spotlight?.href as string}>
                  <div
                    className={styles.button.primary}
                    // style={{ color: spotlight?.color }}
                  >
                    <div className={styles.body.actions.button.content}>
                      <span className="flex flex-row items-center gap-x-1">
                        {t('view.home.label.discoverEvent')}
                        {/* <Icon type="sharp" accessor="arrow-up-right" /> */}
                      </span>
                    </div>
                  </div>
                </Link>

                {/* {event?.content_section?.facebook_event_url && (
                  <Link
                    href={event?.content_section?.facebook_event_url}
                    target="_blank"
                  >
                    <div
                      className={styles.button.secondary}
                      style={{ backgroundColor: color }}
                    >
                      <div className={styles.body.actions.button.content}>
                        <Icon type="brands" accessor="facebook" />
                        <div className="flex flex-row items-center gap-x-1">
                          <span>
                            {t('view.events.detail.label.attendFacebookEvent')}
                          </span>
                          <Icon type="sharp" accessor="arrow-up-right" />
                        </div>
                      </div>
                    </div>
                  </Link>
                )} */}
              </div>
            </div>

            {/* {event?.content_section?.youtube_video_id && (
              <div className={styles.body.video.container}>
                <Dialog>
                  <DialogTrigger className="group">
                    <FadeInAnimation as="div">
                      <Icon
                        accessor="play"
                        size="24px"
                        className={styles.body.icon}
                        type="sharp-light"
                      />
                    </FadeInAnimation>
                  </DialogTrigger>
                  <DialogContent className={styles.dialog.content}>
                    <YouTubeEmbed
                      videoid={event?.content_section?.youtube_video_id}
                      params="controls=0&autoplay=1"
                    />
                  </DialogContent>
                </Dialog>
              </div>
            )} */}
          </div>
        </PageBody>
      </div>
    </>
  );
};

export default HomeHero;
